import { Select } from 'antd';
import {
  CALCULATOR_PICKER_LABEL,
  CALCULATOR_PICKER_PLACEHOLDER,
} from 'constants/uiConstants';
import React, { useMemo } from 'react';
import { CalculatorId, groupCalculators } from 'utils/calculators';
import MergeClassNames from 'utils/MergeClassNames';

interface PickerOption {
  value: CalculatorId;
  label: string;
  description: string;
}

interface PickerGroup {
  label: string;
  title: string;
  options: PickerOption[];
}

const matches = (input: string, option?: PickerOption | PickerGroup) => {
  if (!option || !('value' in option)) return false;
  const query = input.trim().toLowerCase();
  return (
    option.label.toLowerCase().includes(query) ||
    option.description.toLowerCase().includes(query)
  );
};

interface CalculatorPickerProps {
  value: CalculatorId;
  onChange: (id: CalculatorId) => void;
  size?: 'small' | 'middle';
  className?: string;
}

/**
 * Chooses a calculator (OP-2585) — on the Calculators page and in the RH
 * panel. A searchable, grouped dropdown rather than tabs, so it still works
 * when there are many calculators. Each option shows a one-line description.
 */
const CalculatorPicker: React.FC<CalculatorPickerProps> = ({
  value,
  onChange,
  size = 'middle',
  className,
}) => {
  const groups = useMemo<PickerGroup[]>(
    () =>
      groupCalculators().map(({ group, calculators }) => ({
        label: group,
        title: group,
        options: calculators.map(({ id, name, description }) => ({
          value: id,
          label: name,
          description,
        })),
      })),
    []
  );

  return (
    <Select<CalculatorId, PickerOption | PickerGroup>
      aria-label={CALCULATOR_PICKER_LABEL}
      className={MergeClassNames('min-w-0', className)}
      size={size}
      value={value}
      options={groups}
      placeholder={CALCULATOR_PICKER_PLACEHOLDER}
      showSearch={{ filterOption: matches }}
      popupMatchSelectWidth={false}
      optionRender={(option) => (
        <div className="flex flex-col py-0.5">
          <span className="font-semibold text-charcoal">
            {option.data.label}
          </span>
          {'description' in option.data && (
            <span className="text-sm text-gray-muted">
              {option.data.description}
            </span>
          )}
        </div>
      )}
      onChange={onChange}
    />
  );
};

export default CalculatorPicker;
